import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  FormControl,
  FormLabel,
  Switch,
  Input,
  HStack,
  Text,
  Box,
  useToast,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Flex,
  Icon,
  Spinner,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  VStack,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import {
  RiLineChartLine,
  RiNotification3Line,
  RiTimeLine,
  RiMailLine,
  RiInformationLine,
  RiCheckLine,
} from 'react-icons/ri';
import { IoSettingsOutline } from 'react-icons/io5';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import {
  UpdateFrequency,
  NotificationChannel,
  NotificationFrequency,
  useGetProjectSettingsQuery,
  useUpdateProjectSettingsMutation,
} from '../../generated/graphql';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';

interface ProjectSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectTemplateId: string;
  projectName: string;
}

interface SettingsSectionProps {
  icon: any;
  title: string;
  description?: string;
  children: React.ReactNode;
  isEnabled?: boolean;
  onToggle?: (enabled: boolean) => void;
  toggleId?: string;
}

const SettingsSection = ({
  icon,
  title,
  description,
  children,
  isEnabled,
  onToggle,
  toggleId,
}: SettingsSectionProps) => (
  <Box
    bg="white"
    borderRadius="12px"
    border="1px solid"
    borderColor={HelivanColor.border.primary}
    overflow="hidden"
  >
    <Flex
      p={4}
      alignItems="center"
      justifyContent="space-between"
      borderBottom={isEnabled !== false ? '1px solid' : 'none'}
      borderColor={HelivanColor.border.primary}
    >
      <HStack spacing={3}>
        <Flex
          w="32px"
          h="32px"
          borderRadius="8px"
          bg={HelivanColor.hover.lightBlue}
          align="center"
          justify="center"
        >
          <Icon as={icon} boxSize="16px" color={HelivanColor.text.helivanBlue} />
        </Flex>
        <Box>
          <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
            {title}
          </Text>
          {description && (
            <Text fontSize="12px" color={HelivanColor.text.blueMid}>
              {description}
            </Text>
          )}
        </Box>
      </HStack>
      {onToggle !== undefined && (
        <Switch
          id={toggleId}
          isChecked={isEnabled}
          onChange={(e) => onToggle(e.target.checked)}
          colorScheme="blue"
          size="md"
        />
      )}
    </Flex>
    {isEnabled !== false && <Box p={4}>{children}</Box>}
  </Box>
);

const SettingRow = ({
  label,
  description,
  children,
  tooltip,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
  tooltip?: string;
}) => (
  <Flex
    flexDirection={{ base: 'column', md: 'row' }}
    alignItems={{ base: 'stretch', md: 'center' }}
    justifyContent="space-between"
    gap={{ base: 2, md: 4 }}
  >
    <Box flex={1}>
      <HStack spacing={1}>
        <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>
          {label}
        </Text>
        {tooltip && (
          <Tooltip label={tooltip} placement="top" hasArrow>
            <span>
              <Icon as={RiInformationLine} boxSize="14px" color={HelivanColor.text.blueMid} cursor="help" />
            </span>
          </Tooltip>
        )}
      </HStack>
      {description && (
        <Text fontSize="11px" color={HelivanColor.text.blueMid}>
          {description}
        </Text>
      )}
    </Box>
    <Box flexShrink={0} width={{ base: '100%', md: 'auto' }}>{children}</Box>
  </Flex>
);

// Styled dropdown component
const StyledSelect = ({
  value,
  options,
  onChange,
  width = '140px',
}: {
  value: string | number;
  options: { value: string | number; label: string }[];
  onChange: (value: string) => void;
  width?: string;
}) => {
  const selectedOption = options.find((o) => o.value === value);

  return (
    <Menu isLazy>
      <MenuButton
        as={Box}
        px={3}
        py={1.5}
        borderRadius="6px"
        bg="white"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        cursor="pointer"
        minWidth={{ base: '100%', md: width }}
        _hover={{ borderColor: HelivanColor.text.helivanBlue }}
      >
        <HStack justify="space-between">
          <Text fontSize="13px" color={HelivanColor.text.blueDark}>
            {selectedOption?.label || value}
          </Text>
          <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
        </HStack>
      </MenuButton>
      <Portal>
        <MenuList
          zIndex={1500}
          boxShadow="0 4px 20px rgba(0,0,0,0.15)"
          borderRadius="8px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          py={1}
          minW={width}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onChange(String(option.value))}
              fontSize="13px"
              py={2}
              px={3}
              bg={value === option.value ? HelivanColor.hover.lightBlue : 'white'}
              _hover={{ bg: HelivanColor.background.secondary }}
            >
              <HStack justify="space-between" width="full">
                <Text color={HelivanColor.text.blueDark}>{option.label}</Text>
                {value === option.value && (
                  <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />
                )}
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export const ProjectSettingsModal = ({
  isOpen,
  onClose,
  projectTemplateId,
  projectName,
}: ProjectSettingsModalProps) => {
  const toast = useToast();
  const { data, loading: queryLoading } = useGetProjectSettingsQuery({
    variables: { projectTemplateId },
    skip: !isOpen,
  });

  const [updateSettings, { loading: updateLoading }] =
    useUpdateProjectSettingsMutation();

  // Local state for form
  const [updateFrequency, setUpdateFrequency] = useState<UpdateFrequency>(
    UpdateFrequency.Manual
  );
  const [customUpdateIntervalHours, setCustomUpdateIntervalHours] = useState<number>(12);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(true);
  const [notifyOnNewInsights, setNotifyOnNewInsights] = useState(false);
  const [notificationFrequency, setNotificationFrequency] = useState<NotificationFrequency>(
    NotificationFrequency.EveryUpdate
  );
  const [summaryIntervalHours, setSummaryIntervalHours] = useState(24);
  const [notificationEmail, setNotificationEmail] = useState<string>('');

  // Change Detection state
  const [changeDetectionEnabled, setChangeDetectionEnabled] = useState(true);
  const [sensitivityStdDevs, setSensitivityStdDevs] = useState(2.0);
  const [burnInPeriod, setBurnInPeriod] = useState(5);
  const [qualityTrackingEnabled, setQualityTrackingEnabled] = useState(true);

  // Project name state
  const [name, setName] = useState(projectName);

  // Reset name when modal opens with new project
  useEffect(() => {
    setName(projectName);
  }, [projectName, isOpen]);

  // Populate form when data loads
  useEffect(() => {
    if (data?.getProjectSettings) {
      const settings = data.getProjectSettings;
      setUpdateFrequency(settings.updateFrequency);
      setCustomUpdateIntervalHours(settings.customUpdateIntervalHours ?? 12);
      setAutoUpdateEnabled(settings.autoUpdateEnabled);
      setNotificationsEnabled(settings.notificationsEnabled);
      setNotifyOnCompletion(settings.notifyOnCompletion);
      setNotifyOnNewInsights(settings.notifyOnNewInsights);
      setNotificationFrequency(settings.notificationFrequency);
      setSummaryIntervalHours(settings.summaryIntervalHours ?? 24);
      setNotificationEmail(settings.notificationEmail ?? '');
      setChangeDetectionEnabled(settings.changeDetectionEnabled);
      setSensitivityStdDevs(settings.sensitivityStdDevs);
      setBurnInPeriod(settings.burnInPeriod);
      setQualityTrackingEnabled(settings.qualityTrackingEnabled);
    }
  }, [data]);

  const handleSave = async () => {
    try {
      await updateSettings({
        variables: {
          input: {
            projectTemplateId,
            name: name !== projectName ? name : undefined,
            updateFrequency,
            customUpdateIntervalHours:
              updateFrequency === UpdateFrequency.Custom ? customUpdateIntervalHours : null,
            autoUpdateEnabled,
            notificationFrequency,
            summaryIntervalHours:
              notificationFrequency === NotificationFrequency.Interval ? summaryIntervalHours : null,
            notificationsEnabled,
            notifyOnCompletion,
            notifyOnErrors: false,
            notifyOnNewInsights,
            notificationChannels: [NotificationChannel.Email],
            notificationEmail: notificationEmail || null,
            changeDetectionEnabled,
            sensitivityStdDevs,
            burnInPeriod,
            qualityTrackingEnabled,
          },
        },
        // Always refetch the watch's settings query so the live Monitor control band
        // (which uses sensitivityStdDevs/burnInPeriod) updates without a page refresh.
        refetchQueries: name !== projectName
          ? ['ProjectTemplates', 'GetProjectSettings']
          : ['GetProjectSettings'],
        awaitRefetchQueries: true,
      });

      toast({
        title: 'Settings saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error saving settings',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getSensitivityLabel = (value: number) => {
    if (value <= 1) return 'Very High';
    if (value <= 1.5) return 'High';
    if (value <= 2.5) return 'Medium';
    if (value <= 3.5) return 'Low';
    return 'Very Low';
  };

  const frequencyOptions = [
    { value: UpdateFrequency.ThirtyMinutes, label: 'Every 30 minutes' },
    { value: UpdateFrequency.Hourly, label: 'Hourly' },
    { value: UpdateFrequency.SixHours, label: 'Every 6 hours' },
    { value: UpdateFrequency.Daily, label: 'Daily' },
    { value: UpdateFrequency.Weekly, label: 'Weekly' },
    { value: UpdateFrequency.Custom, label: 'Custom' },
  ];

  const baselineOptions = [
    { value: 5, label: '5 points' },
    { value: 10, label: '10 points' },
    { value: 15, label: '15 points' },
    { value: 20, label: '20 points' },
    { value: 30, label: '30 points' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent
        bg={HelivanColor.background.secondary}
        h={{ base: '100dvh', md: 'auto' }}
        maxH={{ base: '100dvh', md: '85vh' }}
        borderRadius={{ base: 0, md: '12px' }}
      >
        <ModalHeader
          py={4}
          px={5}
          bg="white"
          borderBottom="1px solid"
          borderColor={HelivanColor.border.primary}
          borderTopRadius="12px"
        >
          <HStack spacing={3}>
            <Flex
              w="36px"
              h="36px"
              borderRadius="8px"
              bg={HelivanColor.hover.lightBlue}
              align="center"
              justify="center"
            >
              <IoSettingsOutline size={18} color={HelivanColor.text.helivanBlue} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontSize="15px" fontWeight={600} color={HelivanColor.text.blueDark}>
                Project Settings
              </Text>
              <Text fontSize="12px" color={HelivanColor.text.blueMid} fontWeight={400} noOfLines={1} maxW="280px">
                {projectName}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />
        <ModalBody py={4} px={4}>
          {queryLoading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="lg" color={HelivanColor.text.helivanBlue} />
            </Flex>
          ) : (
            <Stack spacing={3}>
              {/* Project Name Section */}
              <Box
                bg="white"
                borderRadius="12px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                p={4}
              >
                <FormControl>
                  <FormLabel fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark} mb={2}>
                    Project Name
                  </FormLabel>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter project name"
                    size="sm"
                    borderRadius="6px"
                    borderColor={HelivanColor.border.primary}
                    _focus={{ borderColor: HelivanColor.text.helivanBlue, boxShadow: `0 0 0 1px ${HelivanColor.text.helivanBlue}` }}
                  />
                </FormControl>
              </Box>

              {/* Auto Update Section */}
              <SettingsSection
                icon={RiTimeLine}
                title="Automatic Updates"
                description="Schedule data refreshes"
                isEnabled={autoUpdateEnabled}
                onToggle={setAutoUpdateEnabled}
                toggleId="auto-update"
              >
                <Stack spacing={4}>
                  <Box>
                    <Text fontSize="12px" fontWeight={500} color={HelivanColor.text.blueMid} mb={2}>
                      Frequency
                    </Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {frequencyOptions.map((option) => (
                        <Box
                          key={option.value}
                          px={3}
                          py={1.5}
                          borderRadius="6px"
                          bg={updateFrequency === option.value ? HelivanColor.hover.lightBlue : 'white'}
                          border="1px solid"
                          borderColor={updateFrequency === option.value ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
                          cursor="pointer"
                          onClick={() => setUpdateFrequency(option.value as UpdateFrequency)}
                          _hover={{ borderColor: HelivanColor.text.helivanBlue }}
                          transition="all 0.15s"
                        >
                          <Text
                            fontSize="13px"
                            fontWeight={updateFrequency === option.value ? 500 : 400}
                            color={updateFrequency === option.value ? HelivanColor.text.helivanBlue : HelivanColor.text.blueDark}
                          >
                            {option.label}
                          </Text>
                        </Box>
                      ))}
                    </Flex>
                  </Box>

                  {updateFrequency === UpdateFrequency.Custom && (
                    <Box>
                      <Flex justify="space-between" mb={2}>
                        <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>
                          Interval
                        </Text>
                        <Box px={2} py={0.5} bg={HelivanColor.hover.lightBlue} borderRadius="4px">
                          <Text fontSize="12px" color={HelivanColor.text.helivanBlue} fontWeight={600}>
                            {customUpdateIntervalHours < 24
                              ? `Every ${customUpdateIntervalHours}h`
                              : customUpdateIntervalHours % 24 === 0
                                ? `Every ${customUpdateIntervalHours / 24}d`
                                : `Every ${customUpdateIntervalHours}h`}
                          </Text>
                        </Box>
                      </Flex>
                      <Flex flexWrap="wrap" gap={2} mb={3}>
                        {[2, 4, 8, 12, 24, 48, 72, 168].map((hours) => (
                          <Box
                            key={hours}
                            px={2.5}
                            py={1}
                            borderRadius="6px"
                            bg={customUpdateIntervalHours === hours ? HelivanColor.hover.lightBlue : 'white'}
                            border="1px solid"
                            borderColor={customUpdateIntervalHours === hours ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
                            cursor="pointer"
                            onClick={() => setCustomUpdateIntervalHours(hours)}
                            _hover={{ borderColor: HelivanColor.text.helivanBlue }}
                            transition="all 0.15s"
                          >
                            <Text
                              fontSize="12px"
                              fontWeight={customUpdateIntervalHours === hours ? 500 : 400}
                              color={customUpdateIntervalHours === hours ? HelivanColor.text.helivanBlue : HelivanColor.text.blueDark}
                            >
                              {hours < 24 ? `${hours}h` : `${hours / 24}d`}
                            </Text>
                          </Box>
                        ))}
                      </Flex>
                      <Box px={1}>
                        <Slider
                          value={customUpdateIntervalHours}
                          min={1}
                          max={168}
                          step={1}
                          onChange={setCustomUpdateIntervalHours}
                          colorScheme="blue"
                        >
                          <SliderTrack bg={HelivanColor.background.secondary} h="6px" borderRadius="full">
                            <SliderFilledTrack bg={HelivanColor.text.helivanBlue} />
                          </SliderTrack>
                          <SliderThumb
                            boxSize="16px"
                            bg="white"
                            border="2px solid"
                            borderColor={HelivanColor.text.helivanBlue}
                            _focus={{ boxShadow: `0 0 0 3px ${HelivanColor.hover.lightBlue}` }}
                          />
                        </Slider>
                        <Flex justify="space-between" mt={1}>
                          <Text fontSize="10px" color={HelivanColor.text.blueMid}>1h</Text>
                          <Text fontSize="10px" color={HelivanColor.text.blueMid}>7d</Text>
                        </Flex>
                      </Box>
                    </Box>
                  )}

                  <Flex
                    p={3}
                    bg={HelivanColor.background.secondary}
                    borderRadius="8px"
                    justifyContent="space-between"
                    fontSize="12px"
                    color={HelivanColor.text.blueMid}
                  >
                    <HStack spacing={1}>
                      <Text fontWeight={500}>Last:</Text>
                      <Text>{formatDate(data?.getProjectSettings?.lastUpdatedAt)}</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Text fontWeight={500}>Next:</Text>
                      <Text>{formatDate(data?.getProjectSettings?.nextScheduledUpdate)}</Text>
                    </HStack>
                  </Flex>
                </Stack>
              </SettingsSection>

              {/* Change Detection Section */}
              <SettingsSection
                icon={RiLineChartLine}
                title="Change Detection"
                description="Monitor for behavioral shifts"
                isEnabled={changeDetectionEnabled}
                onToggle={setChangeDetectionEnabled}
                toggleId="change-detection"
              >
                <Stack spacing={4}>
                  <Box>
                    <Flex justify="space-between" mb={3}>
                      <HStack spacing={1}>
                        <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>
                          Sensitivity
                        </Text>
                        <Tooltip label="Higher = more alerts for smaller changes" placement="top" hasArrow>
                          <span>
                            <Icon as={RiInformationLine} boxSize="14px" color={HelivanColor.text.blueMid} cursor="help" />
                          </span>
                        </Tooltip>
                      </HStack>
                      <Box
                        px={2}
                        py={0.5}
                        bg={HelivanColor.hover.lightBlue}
                        borderRadius="4px"
                      >
                        <Text fontSize="12px" color={HelivanColor.text.helivanBlue} fontWeight={600}>
                          {getSensitivityLabel(sensitivityStdDevs)}
                        </Text>
                      </Box>
                    </Flex>
                    <Box px={1}>
                      <Slider
                        value={sensitivityStdDevs}
                        min={0.5}
                        max={4}
                        step={0.5}
                        onChange={setSensitivityStdDevs}
                        colorScheme="blue"
                      >
                        <SliderTrack bg={HelivanColor.background.secondary} h="6px" borderRadius="full">
                          <SliderFilledTrack bg={HelivanColor.text.helivanBlue} />
                        </SliderTrack>
                        <SliderThumb
                          boxSize="16px"
                          bg="white"
                          border="2px solid"
                          borderColor={HelivanColor.text.helivanBlue}
                          _focus={{ boxShadow: `0 0 0 3px ${HelivanColor.hover.lightBlue}` }}
                        />
                      </Slider>
                      <Flex justify="space-between" mt={1}>
                        <Text fontSize="10px" color={HelivanColor.text.blueMid}>More alerts</Text>
                        <Text fontSize="10px" color={HelivanColor.text.blueMid}>Fewer alerts</Text>
                      </Flex>
                    </Box>
                  </Box>

                  <SettingRow
                    label="Baseline period"
                    tooltip="Data points for calculating normal behavior"
                  >
                    <StyledSelect
                      value={burnInPeriod}
                      options={baselineOptions}
                      onChange={(v) => setBurnInPeriod(parseInt(v))}
                      width="110px"
                    />
                  </SettingRow>

                  <SettingRow
                    label="Track plan quality"
                    tooltip="Planning-probe watches only: also monitor the LLM-judge plan-quality score over time as a second change signal."
                  >
                    <Switch
                      id="qualityTracking"
                      isChecked={qualityTrackingEnabled}
                      onChange={(e) => setQualityTrackingEnabled(e.target.checked)}
                      colorScheme="blue"
                      size="md"
                    />
                  </SettingRow>
                </Stack>
              </SettingsSection>

              {/* Notifications Section */}
              <SettingsSection
                icon={RiNotification3Line}
                title="Monitoring emails"
                description="How often do you want monitoring summaries?"
                isEnabled={true}
              >
                <Stack spacing={4}>
                  <Stack spacing={2}>
                    {[
                      { value: NotificationFrequency.EveryUpdate, label: 'Every update', desc: 'Email after each completed check-in' },
                      { value: NotificationFrequency.Interval, label: 'On a schedule', desc: 'A digest summarizing recent updates' },
                      { value: NotificationFrequency.OnChange, label: 'Only on detected changes', desc: 'Email only when behavior changes are flagged' },
                      { value: NotificationFrequency.Never, label: 'Never', desc: 'No monitoring emails' },
                    ].map((opt) => {
                      const selected = notificationFrequency === opt.value;
                      return (
                        <Box
                          key={opt.value}
                          p={3}
                          borderRadius="8px"
                          border="1px solid"
                          borderColor={selected ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
                          bg={selected ? HelivanColor.hover.lightBlue : 'white'}
                          cursor="pointer"
                          onClick={() => setNotificationFrequency(opt.value)}
                          _hover={{ borderColor: HelivanColor.text.helivanBlue }}
                          transition="all 0.15s"
                        >
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>{opt.label}</Text>
                              <Text fontSize="11px" color={HelivanColor.text.blueMid}>{opt.desc}</Text>
                            </VStack>
                            {selected && <RiCheckLine size={18} color={HelivanColor.text.helivanBlue} />}
                          </HStack>
                        </Box>
                      );
                    })}
                  </Stack>

                  {notificationFrequency === NotificationFrequency.Interval && (
                    <FormControl>
                      <FormLabel fontSize="12px" fontWeight={500} color={HelivanColor.text.blueMid} mb={1}>
                        Summary every
                      </FormLabel>
                      <HStack>
                        <Input
                          type="number"
                          min={1}
                          value={summaryIntervalHours}
                          onChange={(e) => setSummaryIntervalHours(Math.max(1, parseInt(e.target.value) || 1))}
                          size="sm"
                          width="90px"
                          bg="white"
                          borderRadius="6px"
                          borderColor={HelivanColor.border.primary}
                        />
                        <Text fontSize="13px" color={HelivanColor.text.blueMid}>hours</Text>
                      </HStack>
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel fontSize="12px" fontWeight={500} color={HelivanColor.text.blueMid} mb={1}>
                      <HStack spacing={1}>
                        <Icon as={RiMailLine} boxSize="14px" />
                        <span>Email address</span>
                      </HStack>
                    </FormLabel>
                    <Input
                      value={notificationEmail}
                      onChange={(e) => setNotificationEmail(e.target.value)}
                      placeholder="Leave blank for account email"
                      size="sm"
                      bg="white"
                      borderRadius="6px"
                      borderColor={HelivanColor.border.primary}
                      _hover={{ borderColor: HelivanColor.text.helivanBlue }}
                      _placeholder={{ color: HelivanColor.text.blueMid, fontSize: '12px' }}
                    />
                  </FormControl>
                </Stack>
              </SettingsSection>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter
          bg="white"
          borderTop="1px solid"
          borderColor={HelivanColor.border.primary}
          borderBottomRadius="12px"
          py={3}
          px={5}
        >
          <HStack spacing={3}>
            <HelivanButton variant="outline" onClick={onClose} size="sm">
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleSave}
              isLoading={updateLoading}
              isDisabled={queryLoading}
              size="sm"
            >
              Save Changes
            </HelivanButton>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
